import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import {
  ArrowLeft,
  Trash2,
  AlertTriangle,
  User,
  Mail,
  GraduationCap,
  Star,
  Search,
  Users,
  ShieldAlert,
  ChevronRight,
} from "lucide-react";
import { useToast } from "src/components/ui/UseToast";
import { supabase } from "src/lib/supabaseClient";

interface TutorDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePictureUrl?: string | null;
  specializations: string[];
  experience: number;
  rating: number;
  totalSessions: number;
  totalStudents: number;
  activeSessions: number;
  status: string;
  createdAt: string;
}

interface TutorListItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePictureUrl?: string | null;
  subjects: string[];
  status: string;
  rating: number;
}

const DeleteTutor = () => {
  const navigate = useNavigate();
  const { tutorId: routeTutorId } = useParams<{ tutorId: string }>();
  const [searchParams] = useSearchParams();
  const tutorId = routeTutorId || searchParams.get("id");
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();

  const [tutor, setTutor] = useState<TutorDetails | null>(null);
  const [loading, setLoading] = useState(Boolean(tutorId));
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // State for tutor selection when no tutorId is provided
  const [tutorsList, setTutorsList] = useState<TutorListItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (tutorId) {
      fetchTutor(tutorId);
    } else {
      setLoading(false);
      setTutor(null);
      fetchTutorsList();
    }
  }, [tutorId]);

  useEffect(() => {
    if (!tutorId) {
      setTitle("Delete Tutor");
      setDescription("Select a tutor account to permanently remove from the platform");
      setActions(
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/admin/tutors/list")}
          className="flex items-center gap-1.5 text-xs text-slate-700 border-slate-200 hover:bg-slate-50"
        >
          <ArrowLeft size={14} />
          Back to Tutors List
        </Button>
      );
    } else if (tutor) {
      setTitle(`Delete ${tutor.firstName} ${tutor.lastName}`);
      setDescription("Permanently remove tutor account and all associated data");
      setActions(
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/tutors/delete")}
            className="flex items-center gap-1.5 text-xs text-slate-700 border-slate-200 hover:bg-slate-50"
          >
            Select Another Tutor
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/tutors/view/${tutor.id}`)}
            className="flex items-center gap-1.5 text-xs text-slate-700 border-slate-200 hover:bg-slate-50"
          >
            <ArrowLeft size={14} />
            Back to Tutor
          </Button>
        </div>
      );
    } else {
      setTitle("Delete Tutor");
      setDescription("Manage tutor account deletion");
      setActions(
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/admin/tutors/list")}
          className="flex items-center gap-1.5 text-xs text-slate-700 border-slate-200 hover:bg-slate-50"
        >
          <ArrowLeft size={14} />
          Back to Tutors List
        </Button>
      );
    }

    return () => reset();
  }, [tutorId, tutor, setTitle, setDescription, setActions, reset, navigate]);

  const fetchTutorsList = async () => {
    try {
      setListLoading(true);
      const { data, error } = await supabase
        .from("tutors")
        .select(`
          id,
          subjects_taught,
          verification_status,
          rating,
          profile:profiles!tutors_id_fkey (
            first_name,
            last_name,
            email,
            profile_picture_url
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const items: TutorListItem[] = (data || []).map((t: any) => {
        const rawProfile = Array.isArray(t.profile) ? t.profile[0] : t.profile;
        const profile = rawProfile as {
          first_name?: string | null;
          last_name?: string | null;
          email?: string | null;
          profile_picture_url?: string | null;
        } | null;

        let subjects: string[] = [];
        if (Array.isArray(t.subjects_taught)) {
          subjects = t.subjects_taught;
        } else if (typeof t.subjects_taught === "string") {
          try {
            subjects = JSON.parse(t.subjects_taught);
          } catch {
            subjects = t.subjects_taught
              .split(",")
              .map((s: string) => s.trim())
              .filter(Boolean);
          }
        }

        return {
          id: t.id,
          firstName: profile?.first_name || "Unknown",
          lastName: profile?.last_name || "Tutor",
          email: profile?.email || "No email",
          profilePictureUrl: profile?.profile_picture_url || null,
          subjects,
          status: t.verification_status || "active",
          rating: Number(t.rating) || 5.0,
        };
      });

      setTutorsList(items);
    } catch (error: any) {
      console.error("Failed to load tutors list:", error);
      toast({
        title: "Error",
        description: "Failed to load tutors directory.",
        variant: "destructive",
      });
    } finally {
      setListLoading(false);
    }
  };

  const fetchTutor = async (id: string) => {
    try {
      setLoading(true);
      const { data: tutorData, error: tutorError } = await supabase
        .from("tutors")
        .select(`
          id,
          education,
          hourly_rate,
          subjects_taught,
          experience,
          total_years_exp,
          bio,
          verification_status,
          rating,
          created_at,
          profile:profiles!tutors_id_fkey (
            first_name,
            last_name,
            email,
            profile_picture_url
          )
        `)
        .eq("id", id)
        .maybeSingle();

      if (tutorError) throw tutorError;

      if (!tutorData) {
        setTutor(null);
        return;
      }

      // Fetch sessions to compute real impact metrics
      const { data: sessionsData } = await supabase
        .from("sessions")
        .select("id, student_id, status")
        .eq("tutor_id", id);

      let subjects: string[] = [];
      if (Array.isArray(tutorData.subjects_taught)) {
        subjects = tutorData.subjects_taught;
      } else if (typeof tutorData.subjects_taught === "string") {
        try {
          subjects = JSON.parse(tutorData.subjects_taught);
        } catch {
          subjects = tutorData.subjects_taught
            .split(",")
            .map((s: string) => s.trim())
            .filter(Boolean);
        }
      }

      let experienceYears = 0;
      if (typeof tutorData.total_years_exp === "number") {
        experienceYears = tutorData.total_years_exp;
      } else if (Array.isArray(tutorData.experience) && tutorData.experience.length > 0) {
        experienceYears = Number(tutorData.experience[0]?.years) || tutorData.experience.length;
      }

      const uniqueStudents = new Set(
        (sessionsData || []).map((s: any) => s.student_id).filter(Boolean)
      );

      const activeSessionsCount = (sessionsData || []).filter((s: any) =>
        ["scheduled", "in_progress", "confirmed"].includes(s.status)
      ).length;

      const rawProfile = Array.isArray(tutorData.profile) ? tutorData.profile[0] : tutorData.profile;
      const profile = rawProfile as {
        first_name?: string | null;
        last_name?: string | null;
        email?: string | null;
        profile_picture_url?: string | null;
      } | null;

      setTutor({
        id: tutorData.id,
        firstName: profile?.first_name || "Unknown",
        lastName: profile?.last_name || "Tutor",
        email: profile?.email || "No email",
        profilePictureUrl: profile?.profile_picture_url,
        specializations: subjects,
        experience: experienceYears,
        rating: Number(tutorData.rating) || 5.0,
        totalSessions: sessionsData?.length || 0,
        totalStudents: uniqueStudents.size,
        activeSessions: activeSessionsCount,
        status: tutorData.verification_status || "active",
        createdAt: tutorData.created_at || new Date().toISOString(),
      });
    } catch (error: any) {
      console.error("Failed to fetch tutor details:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch tutor details. Please try again.",
        variant: "destructive",
      });
      setTutor(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTutor = async () => {
    if (!tutor) return;

    const expectedText = `DELETE ${tutor.firstName} ${tutor.lastName}`.trim();
    if (confirmText.trim() !== expectedText) {
      toast({
        title: "Confirmation Required",
        description: `Please type "${expectedText}" to confirm deletion.`,
        variant: "destructive",
      });
      return;
    }

    setDeleting(true);

    try {
      // 1. Clean up availability slots first to avoid foreign key constraints
      const { error: availabilityError } = await supabase
        .from("tutor_availability")
        .delete()
        .eq("tutor_id", tutor.id);

      if (availabilityError) {
        console.warn("Failed to clear tutor availability before delete:", availabilityError);
      }

      // 2. Delete the tutor record
      const { error: deleteError } = await supabase
        .from("tutors")
        .delete()
        .eq("id", tutor.id);

      if (deleteError) throw deleteError;

      toast({
        title: "Success",
        description: `Tutor ${tutor.firstName} ${tutor.lastName} was permanently deleted.`,
        variant: "default",
      });

      navigate("/admin/tutors/list");
    } catch (error: any) {
      console.error("Failed to delete tutor:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete tutor. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const filteredTutors = useMemo(() => {
    if (!searchQuery.trim()) return tutorsList;
    const q = searchQuery.toLowerCase();
    return tutorsList.filter(
      (t) =>
        t.firstName.toLowerCase().includes(q) ||
        t.lastName.toLowerCase().includes(q) ||
        t.email.toLowerCase().includes(q) ||
        t.subjects.some((s) => s.toLowerCase().includes(q))
    );
  }, [tutorsList, searchQuery]);

  const renderRating = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        <span className="text-sm font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };

  // Loading state when an ID is being loaded
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rfpurple-600"></div>
        <p className="text-sm text-slate-500">Loading tutor details...</p>
      </div>
    );
  }

  // View when NO tutorId is provided: Search and select a tutor to delete
  if (!tutorId) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Warning Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <h4 className="font-semibold text-amber-900 mb-0.5">Tutor Deletion Console</h4>
            <p className="text-amber-700">
              Select a tutor below to review their teaching history, impact assessment, and confirm permanent account deletion. Deletions cannot be undone.
            </p>
          </div>
        </div>

        {/* Search & Directory Card */}
        <Card className="border-slate-200 shadow-xs">
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Users className="w-4 h-4 text-rfpurple-600" />
                  Select Tutor to Delete
                </CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Choose a tutor from the directory or search by name, email, or subject
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/tutors/list")}
                className="text-xs text-slate-700 border-slate-200 self-start sm:self-auto"
              >
                Browse All Tutors
              </Button>
            </div>

            {/* Search Input */}
            <div className="mt-3 relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search tutors by name, email, or specialization..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rfpurple-500 focus:border-rfpurple-500 bg-slate-50/50"
              />
            </div>
          </CardHeader>

          <CardContent className="pt-4">
            {listLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-500">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-rfpurple-600"></div>
                <p className="text-xs">Fetching tutors directory...</p>
              </div>
            ) : filteredTutors.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <h4 className="text-sm font-semibold text-slate-700">No tutors found</h4>
                <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                  {searchQuery
                    ? `No tutors matched "${searchQuery}". Try a different search term.`
                    : "There are currently no tutors registered in the system."}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredTutors.map((item) => (
                  <div
                    key={item.id}
                    className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/70 px-2 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-rfpurple-100 text-rfpurple-700 font-bold flex items-center justify-center text-sm shrink-0 border border-rfpurple-200 overflow-hidden">
                        {item.profilePictureUrl ? (
                          <img
                            src={item.profilePictureUrl}
                            alt={`${item.firstName} ${item.lastName}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          `${item.firstName.charAt(0)}${item.lastName.charAt(0)}`
                        )}
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-sm text-slate-900">
                            {item.firstName} {item.lastName}
                          </h4>
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                              item.status === "active" || item.status === "approved"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : item.status === "pending" || item.status === "under_review"
                                ? "bg-amber-50 text-amber-700 border border-amber-200"
                                : "bg-slate-100 text-slate-700 border border-slate-200"
                            }`}
                          >
                            {item.status.replace("_", " ")}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <Mail size={12} className="text-slate-400" />
                          {item.email}
                        </p>
                        {item.subjects.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {item.subjects.slice(0, 3).map((sub) => (
                              <span
                                key={sub}
                                className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded font-medium"
                              >
                                {sub}
                              </span>
                            ))}
                            {item.subjects.length > 3 && (
                              <span className="text-[10px] text-slate-400 font-medium">
                                +{item.subjects.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/tutors/view/${item.id}`)}
                        className="text-xs text-slate-600 border-slate-200 hover:bg-white"
                      >
                        View Profile
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/admin/tutors/delete/${item.id}`)}
                        className="bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 hover:border-rose-300 text-xs font-semibold flex items-center gap-1.5 shadow-2xs"
                      >
                        <Trash2 size={13} />
                        Select to Delete
                        <ChevronRight size={13} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // If tutorId was provided but tutor not found
  if (!tutor) {
    return (
      <div className="max-w-md mx-auto text-center py-12 px-4">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
          <AlertTriangle className="w-6 h-6 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Tutor Not Found</h2>
        <p className="text-sm text-slate-500 mb-6">
          The tutor record with ID <code className="text-xs font-mono bg-slate-100 px-1 py-0.5 rounded">{tutorId}</code> could not be found or has already been removed.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => navigate("/admin/tutors/delete")}>
            Select Another Tutor
          </Button>
          <Button onClick={() => navigate("/admin/tutors/list")}>
            Back to Tutors List
          </Button>
        </div>
      </div>
    );
  }

  const expectedConfirmText = `DELETE ${tutor.firstName} ${tutor.lastName}`.trim();

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Danger Zone Warning */}
      <Card className="border-rose-200 bg-rose-50/60 shadow-xs">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-rose-800 text-base font-semibold">
            <AlertTriangle size={18} />
            Danger Zone: Irreversible Action
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-rose-800 font-medium">
              You are about to permanently delete the tutor account for{" "}
              <span className="font-bold underline">{tutor.firstName} {tutor.lastName}</span>.
            </p>
            <div className="bg-rose-100/70 p-3.5 rounded-lg border border-rose-200">
              <h4 className="text-xs font-bold text-rose-900 uppercase tracking-wider mb-2">
                This deletion will execute the following:
              </h4>
              <ul className="text-xs text-rose-800 space-y-1.5 list-disc list-inside">
                <li>Permanently remove the tutor record and credentials</li>
                <li>Delete all associated weekly availability schedules</li>
                <li>Prevent future bookings or assignments for this tutor</li>
                <li>Historical session logs will become unlinked from active profile</li>
                <li className="font-semibold">This action cannot be undone or restored</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tutor Information */}
      <Card className="border-slate-200 shadow-xs">
        <CardHeader className="pb-3 border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <User size={18} className="text-rfpurple-600" />
            Tutor Record Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Full Name
                </label>
                <p className="text-base font-semibold text-slate-900">
                  {tutor.firstName} {tutor.lastName}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Email Address
                </label>
                <div className="flex items-center gap-1.5">
                  <Mail size={14} className="text-slate-400" />
                  <p className="text-sm text-slate-800">{tutor.email}</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Specializations & Subjects
              </label>
              <div className="flex flex-wrap gap-1.5">
                {tutor.specializations.length > 0 ? (
                  tutor.specializations.map((spec) => (
                    <span
                      key={spec}
                      className="px-2 py-0.5 text-xs bg-rfpurple-50 text-rfpurple-700 border border-rfpurple-200 rounded-md font-medium"
                    >
                      {spec}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-slate-400 italic">No specializations listed</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-slate-100">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Experience
                </label>
                <div className="flex items-center gap-1 text-slate-800 text-sm font-medium">
                  <GraduationCap size={15} className="text-slate-400" />
                  <span>{tutor.experience} {tutor.experience === 1 ? "year" : "years"}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Rating
                </label>
                <div>{renderRating(tutor.rating)}</div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Total Sessions
                </label>
                <p className="text-sm text-slate-900 font-semibold">{tutor.totalSessions}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                  Students Taught
                </label>
                <p className="text-sm text-slate-900 font-semibold">{tutor.totalStudents}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Impact Assessment */}
      <Card className="border-amber-200 bg-amber-50/50 shadow-xs">
        <CardHeader className="pb-3 border-b border-amber-100">
          <CardTitle className="flex items-center gap-2 text-amber-900 text-base font-semibold">
            <AlertTriangle size={18} className="text-amber-600" />
            Impact Assessment
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-2.5 text-xs text-amber-900">
            <div className="flex items-center justify-between pb-1.5 border-b border-amber-200/50">
              <span className="font-medium text-amber-800">Completed Sessions Recorded:</span>
              <span className="font-bold text-amber-950">{tutor.totalSessions} sessions</span>
            </div>
            <div className="flex items-center justify-between pb-1.5 border-b border-amber-200/50">
              <span className="font-medium text-amber-800">Students Coached:</span>
              <span className="font-bold text-amber-950">{tutor.totalStudents} students</span>
            </div>
            <div className="flex items-center justify-between pb-1.5 border-b border-amber-200/50">
              <span className="font-medium text-amber-800">Active / Upcoming Sessions:</span>
              <span className={`font-bold ${tutor.activeSessions > 0 ? "text-rose-700" : "text-amber-950"}`}>
                {tutor.activeSessions} active
              </span>
            </div>
            {tutor.activeSessions > 0 && (
              <div className="bg-rose-100/80 p-2.5 rounded text-rose-800 font-medium text-xs mt-2 border border-rose-200">
                Warning: This tutor has {tutor.activeSessions} scheduled or active session(s). Please reassign their sessions before deleting this tutor account.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Card */}
      <Card className="border-rose-200 shadow-xs">
        <CardHeader className="pb-3 border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-rose-800">
            <Trash2 size={18} />
            Confirm Deletion
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            <p className="text-xs text-slate-700 leading-relaxed">
              To prevent accidental deletion, please type{" "}
              <code className="bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded text-xs font-mono font-bold select-all">
                {expectedConfirmText}
              </code>{" "}
              in the verification box below:
            </p>

            <div>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={expectedConfirmText}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 font-mono"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 pt-3 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/tutors/view/${tutor.id}`)}
                className="text-xs text-slate-700 border-slate-200"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleDeleteTutor}
                disabled={deleting || confirmText.trim() !== expectedConfirmText}
                className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                    Deleting Account...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    Permanently Delete Tutor
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeleteTutor;
